import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { resetPassword } from 'aws-amplify/auth';
import { supabase } from '@/config/supabase';
import { ensureAmplifyConfigured, isCognitoConfigured } from '@/config/cognitoAuth';
import { shouldApplyAuthBypass } from '@/config/environment';
import { healthDbPing, userProfileGet, userExistsByEmail, userRegisterProfile } from '@/services/tradingDbApi';

const AuthModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  buttonText,
  isSignup,
  signupName,
  setSignupName,
  signupPhone,
  setSignupPhone,
  signupEmail,
  setSignupEmail,
  signupPassword,
  setSignupPassword,
  loginInput,
  setLoginInput,
  password,
  setPassword,
  error,
  signupSuccess,
  showPassword,
  setShowPassword,
  forgotMode,
  setForgotMode,
  forgotInput,
  setForgotInput,
  forgotMessage,
  handleForgotPassword,
}: any) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[280px] transform transition-all">
        {/* Header */}
        <div className="relative px-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] rounded-lg flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 tracking-tight">{title}</h2>
                <p className="text-xs text-gray-500">Welcome to Stratible</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="px-4 pb-4">
          {forgotMode ? (
            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div>
                <label htmlFor="forgotInput" className="block text-xs font-semibold text-gray-800 mb-1">Email Address</label>
                <input
                  type="email"
                  id="forgotInput"
                  value={forgotInput}
                  onChange={e => setForgotInput(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-xs"
                  placeholder="Enter your email address"
                />
              </div>
              {forgotMessage && (
                <div className={`text-xs p-1.5 rounded-lg ${forgotMessage.includes('sent') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {forgotMessage}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white py-1.5 px-3 rounded-xl font-semibold hover:from-[#4338ca] hover:to-[#6d28d9] transition-all duration-200 transform hover:scale-[1.02] shadow-lg text-xs"
              >
                Send Reset Link
              </button>
              <button
                type="button"
                className="w-full text-[#4f46e5] font-medium py-1 text-xs hover:text-[#4338ca] transition-colors"
                onClick={() => setForgotMode(false)}
              >
                 Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              {isSignup && (
                <div>
                  <label htmlFor="signupName" className="block text-xs font-semibold text-gray-800 mb-1">Full Name</label>
                  <input
                    type="text"
                    id="signupName"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    required
                    autoComplete="name"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-xs"
                    placeholder="Enter your full name"
                  />
                </div>
              )}
              
              {isSignup && (
                <div>
                  <label htmlFor="signupPhone" className="block text-xs font-semibold text-gray-800 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    id="signupPhone"
                    value={signupPhone}
                    onChange={(e) => setSignupPhone(e.target.value)}
                    required
                    autoComplete="tel"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-xs"
                    placeholder="Enter your phone number"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-800 mb-1">
                  {isSignup ? "Email Address" : "Email or Phone"}
                </label>
                <input
                  type={isSignup ? "email" : "text"}
                  id="email"
                  value={isSignup ? signupEmail : loginInput}
                  onChange={isSignup ? (e) => setSignupEmail(e.target.value) : (e) => setLoginInput(e.target.value)}
                  required
                  autoComplete={isSignup ? "email" : "email"}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-xs"
                  placeholder={isSignup ? "Enter your email address" : "Enter your email or phone"}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-gray-800 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={isSignup ? signupPassword : password}
                    onChange={isSignup ? (e) => setSignupPassword(e.target.value) : (e) => setPassword(e.target.value)}
                    required
                    autoComplete={isSignup ? "new-password" : "current-password"}
                    className="w-full px-2 py-1.5 pr-6 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400 text-xs"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-2 py-1.5 rounded-lg text-xs">
                  {error}
                </div>
              )}

              {signupSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-2 py-1.5 rounded-lg text-xs">
                  Account created! Please verify your email before logging in. Check your inbox (and spam) for the verification link.
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white py-1 px-2 rounded-xl font-semibold hover:from-[#4338ca] hover:to-[#6d28d9] transition-all duration-200 transform hover:scale-[1.02] shadow-lg text-xs h-7 min-h-0"
              >
                {buttonText}
              </button>

              {!isSignup && (
                <button
                  type="button"
                  onClick={() => setForgotMode(true)}
                  className="w-full text-[#4f46e5] font-medium py-1 hover:text-[#4338ca] transition-colors text-xs"
                >
                  Forgot your password?
                </button>
              )}
            </form>
          )}

          {/* Divider */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              {isSignup ? "Already have an account?" : "Don't have an account?"}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  // Trigger the other modal to open with a small delay
                  setTimeout(() => {
                    if (isSignup) {
                      window.dispatchEvent(new CustomEvent('openLoginModal'));
                    } else {
                      window.dispatchEvent(new CustomEvent('openSignupModal'));
                    }
                  }, 100);
                }}
                className="ml-1 text-[#4f46e5] font-semibold hover:text-[#4338ca] transition-colors"
              >
                {isSignup ? "Sign in" : "Sign up"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotInput, setForgotInput] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signupName, setSignupName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [supabaseStatus, setSupabaseStatus] = useState<'ok' | 'error' | 'checking'>('checking');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showLegalDropdown, setShowLegalDropdown] = useState(false);

  const { signIn, signUp, user, signOut } = useAuth();
  const authBypass = shouldApplyAuthBypass();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setShowServicesDropdown(false);
        setShowLegalDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch user profile when user is logged in
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (authBypass && user) {
        setUserProfile({
          full_name: (user as any).user_metadata?.full_name || user.email || 'Admin',
          email: user.email,
        });
        return;
      }
      if (user) {
        const prof = await userProfileGet(
          user.id,
          typeof user.email === 'string' ? user.email : undefined
        );
        if (prof.ok && prof.data) {
          setUserProfile({
            full_name: (prof.data.full_name as string) || user.email || '',
            email: (prof.data.email as string) || user.email || '',
          });
        } else {
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
    };
    fetchUserProfile();
  }, [user, authBypass]);

  // Listen for custom events from EnterpriseLandingPage
  useEffect(() => {
    const handleOpenSignupModal = () => {
      setIsSignupModalOpen(true);
    };

    const handleOpenLoginModal = () => {
      setIsLoginModalOpen(true);
    };

    window.addEventListener('openSignupModal', handleOpenSignupModal);
    window.addEventListener('openLoginModal', handleOpenLoginModal);

    return () => {
      window.removeEventListener('openSignupModal', handleOpenSignupModal);
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
    };
  }, []);

  React.useEffect(() => {
    const testConnection = async () => {
      if (authBypass) {
        setSupabaseStatus('ok');
        return;
      }
      try {
        const db = await healthDbPing();
        setSupabaseStatus(db.ok ? 'ok' : 'error');
      } catch {
        setSupabaseStatus('error');
      }
    };
    testConnection();
  }, [authBypass]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Always use hook signIn so bypass mode never calls Supabase (phone vs email handled in useAuth).
    const result = await signIn(loginInput, password);
    const { error } = result;
    if (error) {
      console.error('Login error details:', error);
      // More specific and user-friendly error messages
      if (error.message.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (error.message.includes('Email not confirmed') || error.message.includes('email_not_confirmed')) {
        setError('Please confirm your email first! Check your inbox and click the confirmation link, then try logging in again.');
      } else if (error.message.includes('Too many requests') || error.message.includes('rate_limit_exceeded')) {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else if (error.message.includes('User not found') || error.message.includes('user_not_found')) {
        setError('No account found with this email. Please sign up first.');
      } else if (error.message.includes('Invalid email') || error.message.includes('invalid_email')) {
        setError('Please enter a valid email address.');
      } else if (error.message.includes('Password') && error.message.includes('weak')) {
        setError('Password is too weak. Please use a stronger password.');
      } else if (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        (error as { name?: string }).name === 'AuthRetryableFetchError'
      ) {
        setError(
          'Cannot reach auth servers (network/DNS). For temporary access: add ?stratible_auth_bypass=1 to the URL once, or set VITE_AUTH_BYPASS_ENABLED=true on Vercel and redeploy.'
        );
      } else {
        setError(`Login failed: ${error.message}`);
      }
    } else {
      setIsLoginModalOpen(false);
      setLoginInput('');
      setPassword('');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotMessage('');
    if (!forgotInput) return;
    if (shouldApplyAuthBypass()) {
      setForgotMessage('Password reset is unavailable while temporary auth bypass is enabled.');
      return;
    }

    if (isCognitoConfigured()) {
      ensureAmplifyConfigured();
      try {
        await resetPassword({ username: forgotInput.trim().toLowerCase() });
        setForgotMessage(
          'If this email is registered, you will receive a verification code. Open the Reset Password page and enter your email, the code, and a new password.'
        );
      } catch (e) {
        setForgotMessage(e instanceof Error ? e.message : 'Could not start password reset.');
      }
      return;
    }

    const resetDomain = 'https://www.stratible.in';
    const { error } = await supabase.auth.resetPasswordForEmail(forgotInput, {
      redirectTo: `${resetDomain}/reset-password`,
    });
    if (error) {
      setForgotMessage(error.message);
    } else {
      setForgotMessage('Password reset email sent! Please check your inbox.');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSignupSuccess(false);
    if (!signupEmail || !signupPassword || signupPassword.length < 6) {
      setError('Please enter a valid email and a password with at least 6 characters.');
      return;
    }
    if (shouldApplyAuthBypass()) {
      setError('Sign-up is disabled while temporary auth bypass is enabled.');
      return;
    }

    const ex = await userExistsByEmail(signupEmail.toLowerCase().trim());
    if (!ex.ok) {
      console.error('Error checking existing user:', ex.error);
      setError('An error occurred while checking for existing users. Please try again.');
      return;
    }
    if (ex.exists) {
      setError('An account with this email already exists. Please try logging in instead.');
      return;
    }

    const { data, error } = await signUp(signupEmail, signupPassword, {
      full_name: signupName,
      phone: signupPhone,
    });
    if (error) {
      // Handle specific Supabase Auth errors
      if (error.message.includes('User already registered') || error.message.includes('duplicate key')) {
        setError('An account with this email already exists. Please try logging in instead.');
      } else {
        setError(error.message);
      }
    } else {
      if (data.user) {
        const ins = await userRegisterProfile({
          id: data.user.id,
          full_name: signupName,
          email: signupEmail,
          phone: signupPhone,
        });
        if (!ins.ok) {
          const msg = ins.error || '';
          if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('email')) {
            setError('An account with this email already exists. Please try logging in instead.');
          } else {
            setError(msg || 'Could not create profile');
          }
          return;
        }
      }
      setSignupSuccess(true);
      setSignupName('');
      setSignupPhone('');
      setSignupPassword('');
      setSignupEmail('');
      // Close modal after 3 seconds to give user time to read the message
      setTimeout(() => {
        setIsSignupModalOpen(false);
        setSignupSuccess(false);
      }, 3000);
    }
  };

  return (
    <header className="w-full bg-white shadow-sm py-0">
      {supabaseStatus === 'error' && (
        <div className="bg-red-100 text-red-700 px-4 py-2 text-center text-sm font-semibold">
          Error: Could not reach trading API database (check VITE_API_URL and EC2 /health/db).
        </div>
      )}
      <AuthModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSubmit={handleLogin}
        title="Login to Stratible"
        buttonText="Sign In"
        isSignup={false}
        signupName={signupName}
        setSignupName={setSignupName}
        signupPhone={signupPhone}
        setSignupPhone={setSignupPhone}
        signupEmail={signupEmail}
        setSignupEmail={setSignupEmail}
        signupPassword={signupPassword}
        setSignupPassword={setSignupPassword}
        loginInput={loginInput}
        setLoginInput={setLoginInput}
        password={password}
        setPassword={setPassword}
        error={error}
        signupSuccess={signupSuccess}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        forgotMode={forgotMode}
        setForgotMode={setForgotMode}
        forgotInput={forgotInput}
        setForgotInput={setForgotInput}
        forgotMessage={forgotMessage}
        handleForgotPassword={handleForgotPassword}
      />
      <AuthModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        onSubmit={handleSignup}
        title="Create Your Stratible Account"
        buttonText="Sign Up"
        isSignup={true}
        signupName={signupName}
        setSignupName={setSignupName}
        signupPhone={signupPhone}
        setSignupPhone={setSignupPhone}
        signupEmail={signupEmail}
        setSignupEmail={setSignupEmail}
        signupPassword={signupPassword}
        setSignupPassword={setSignupPassword}
        loginInput={loginInput}
        setLoginInput={setLoginInput}
        password={password}
        setPassword={setPassword}
        error={error}
        signupSuccess={signupSuccess}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        forgotMode={forgotMode}
        setForgotMode={setForgotMode}
        forgotInput={forgotInput}
        setForgotInput={setForgotInput}
        forgotMessage={forgotMessage}
        handleForgotPassword={handleForgotPassword}
      />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="h-40 w-auto -my-2 flex items-center">
            <img
                              src="/images/logo.png"
              alt="Stratible Logo"
              className="h-60 w-auto object-contain"
              onError={(e) => {
                // Fallback to a text logo if image fails to load
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgNTAiPjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iNTAiIGZpbGw9IiMxYTdiZjIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE2IiBmaWxsPSJ3aGl0ZSIgZm9udC13ZWlnaHQ9ImJvbGQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TVFJBVElCTEU8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
          <div className="flex items-center space-x-4">
            {/* Services Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowServicesDropdown(!showServicesDropdown);
                  setShowLegalDropdown(false);
                }}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span>Services</span>
                <svg className={`w-4 h-4 transition-transform ${showServicesDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showServicesDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowServicesDropdown(false);
                        window.dispatchEvent(new CustomEvent('navigateToView', { detail: { view: 'services' } }));
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Our Services
                    </button>
                    <button
                      onClick={() => {
                        setShowServicesDropdown(false);
                        window.dispatchEvent(new CustomEvent('navigateToView', { detail: { view: 'contact' } }));
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Contact Us
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Legal Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => {
                  setShowLegalDropdown(!showLegalDropdown);
                  setShowServicesDropdown(false);
                }}
                className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span>Legal</span>
                <svg className={`w-4 h-4 transition-transform ${showLegalDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showLegalDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowLegalDropdown(false);
                        window.dispatchEvent(new CustomEvent('navigateToView', { detail: { view: 'terms' } }));
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Terms & Conditions
                    </button>
                    <button
                      onClick={() => {
                        setShowLegalDropdown(false);
                        window.dispatchEvent(new CustomEvent('navigateToView', { detail: { view: 'privacy' } }));
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Privacy Policy
                    </button>
                    <button
                      onClick={() => {
                        setShowLegalDropdown(false);
                        window.dispatchEvent(new CustomEvent('navigateToView', { detail: { view: 'refunds' } }));
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Refunds & Cancellations
                    </button>
                  </div>
                </div>
              )}
            </div>

            {user ? (
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    Welcome, {userProfile?.full_name ? userProfile.full_name.split(' ')[0] : user.email?.split('@')[0]}!
                  </div>
                  <div className="text-xs text-gray-500">
                    {user.email}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await signOut();
                    // Reload the page to reset the application state
                    window.location.href = '/';
                  }}
                  className="bg-red-500 text-white px-3 py-1.5 rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="bg-[#4f46e5] text-white px-6 py-2 rounded-lg hover:bg-[#4338ca] transition-colors font-semibold min-w-[80px]"
                >
                  Login
                </button>
                <button
                  onClick={() => setIsSignupModalOpen(true)}
                  className="border-2 border-[#4f46e5] text-[#4f46e5] px-6 py-2 rounded-lg hover:bg-[#4f46e5] hover:text-white transition-colors font-semibold min-w-[80px]"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
